const COMMS_API_ROOT = '/api/comms';

function authHeaders(extra = {}) {
  const token = localStorage.getItem('access_token');

  return {
    ...(token
      ? { Authorization: `Bearer ${token}` }
      : {}),
    ...extra,
  };
}

async function fetchTurnCredentials() {
  const response = await fetch(
    `${COMMS_API_ROOT}/turn-credentials`,
    {
      method: 'GET',
      headers: authHeaders({
        Accept: 'application/json',
      }),
      cache: 'no-store',
    }
  );

  let body = null;

  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (!response.ok) {
    throw new Error(
      body?.detail ||
      body?.message ||
      `TURN credential request failed with HTTP ${response.status}`
    );
  }

  if (
    !Array.isArray(body?.iceServers) ||
    body.iceServers.length === 0
  ) {
    throw new Error(
      'TURN credential response contained no ICE servers.'
    );
  }

  return body;
}

export class RapidXCommsRtc {
  constructor({
    sendSignal,
    onRemoteStream,
    onPeerState,
    onError,
  } = {}) {
    this.sendSignal = sendSignal;
    this.onRemoteStream = onRemoteStream;
    this.onPeerState = onPeerState;
    this.onError = onError;

    this.channelId = null;

    this.localStream = null;

    this.iceServers = null;
    this.turnExpiresAt = 0;

    this.peers = new Map();
  }

  async ensureTurnCredentials() {
    const nowSeconds = Math.floor(Date.now() / 1000);

    // Refresh with a safety margin before expiry.
    if (
      this.iceServers &&
      this.turnExpiresAt &&
      nowSeconds < this.turnExpiresAt - 60
    ) {
      return this.iceServers;
    }

    const result = await fetchTurnCredentials();

    this.iceServers = result.iceServers;
    this.turnExpiresAt = result.expires_at || 0;

    return this.iceServers;
  }

  async ensureMicrophone() {
    if (
      this.localStream &&
      this.localStream.getAudioTracks().length > 0
    ) {
      return this.localStream;
    }

    if (
      !navigator.mediaDevices ||
      !navigator.mediaDevices.getUserMedia
    ) {
      throw new Error(
        'Microphone capture is not supported by this browser.'
      );
    }

    this.localStream =
      await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });

    // PTT starts muted.
    this.setTransmitEnabled(false);

    /*
     * RTC peers are negotiated with an audio transceiver before
     * microphone permission is requested.
     *
     * Attach the microphone to that already-negotiated sender.
     * replaceTrack() does not introduce a new media section and
     * therefore does not require a competing SDP renegotiation.
     */
    const audioTrack =
      this.localStream.getAudioTracks()[0] || null;

    if (audioTrack) {
      for (const state of this.peers.values()) {
        const sender =
          state?.audioTransceiver?.sender;

        if (!sender) continue;


        await sender.replaceTrack(audioTrack);

      }
    }

    return this.localStream;
  }

  setChannel(channelId) {
    this.channelId = channelId || null;
  }

  setTransmitEnabled(enabled) {
    if (!this.localStream) return;

    for (
      const track of this.localStream.getAudioTracks()
    ) {

      track.enabled = Boolean(enabled);

    }
  }

  async createPeer(
    peerConnectionId,
    {
      initiator = false,
    } = {}
  ) {
    if (!peerConnectionId) {
      throw new Error(
        'RTC peer connection ID is required.'
      );
    }

    const existing =
      this.peers.get(peerConnectionId);

    if (existing) {
      return existing.pc;
    }

    if (!this.channelId) {
      throw new Error(
        'RTC channel is not selected.'
      );
    }

    await this.ensureTurnCredentials();

    /*
     * Do not request microphone access here.
     *
     * createPeer() may be entered from asynchronous RTC
     * signalling, which is outside the user's direct PTT
     * gesture on mobile browsers.
     */
    const pc = new RTCPeerConnection({
      iceServers: this.iceServers,

      // RapidX production voice should use the
      // authenticated TURN service.
      iceTransportPolicy: 'relay',
    });

    /*
     * Negotiate the audio media section immediately, without
     * requesting microphone permission.
     *
     * The microphone track is attached later from the user's
     * PTT gesture with replaceTrack().
     */
    const audioTransceiver =
      pc.addTransceiver('audio', {
        direction: 'sendrecv',
      });

    const state = {
      pc,
      audioTransceiver,
      pendingCandidates: [],
      remoteDescriptionSet: false,
    };

    this.peers.set(
      peerConnectionId,
      state
    );

    /*
     * If microphone access already exists, attach its disabled
     * audio track to the pre-negotiated sender. This does not
     * itself start transmission.
     */
    if (this.localStream) {
      const audioTrack =
        this.localStream.getAudioTracks()[0] || null;

      if (audioTrack) {

        await audioTransceiver.sender.replaceTrack(
          audioTrack
        );

      }
    }

    pc.onicecandidate = (event) => {
      if (!this.sendSignal) return;

      this.sendSignal({
        type: 'rtc_ice_candidate',
        channel_id: this.channelId,
        target_connection_id:
          peerConnectionId,
        candidate: event.candidate
          ? event.candidate.toJSON()
          : null,
      });
    };

    pc.ontrack = (event) => {
      const stream =
        event.streams?.[0] ||
        new MediaStream([event.track]);

      if (this.onRemoteStream) {
        this.onRemoteStream(
          peerConnectionId,
          stream
        );
      }
    };

    const reportState = () => {
      if (!this.onPeerState) return;

      this.onPeerState(
        peerConnectionId,
        {
          connectionState:
            pc.connectionState,
          iceConnectionState:
            pc.iceConnectionState,
          signalingState:
            pc.signalingState,
        }
      );
    };

    pc.onconnectionstatechange =
      reportState;

    pc.oniceconnectionstatechange =
      reportState;

    pc.onsignalingstatechange =
      reportState;

    if (initiator) {
      const offer =
        await pc.createOffer();

      await pc.setLocalDescription(
        offer
      );

      this.sendSignal?.({
        type: 'rtc_offer',
        channel_id: this.channelId,
        target_connection_id:
          peerConnectionId,
        description:
          pc.localDescription.toJSON
            ? pc.localDescription.toJSON()
            : {
                type:
                  pc.localDescription.type,
                sdp:
                  pc.localDescription.sdp,
              },
      });
    }

    return pc;
  }

  async handleOffer(
    sourceConnectionId,
    description
  ) {
    try {
      const pc = await this.createPeer(
        sourceConnectionId,
        {
          initiator: false,
        }
      );

      await pc.setRemoteDescription(
        description
      );

      const state =
        this.peers.get(
          sourceConnectionId
        );

      if (state) {
        state.remoteDescriptionSet =
          true;

        await this.flushPendingCandidates(
          sourceConnectionId
        );
      }

      const answer =
        await pc.createAnswer();

      await pc.setLocalDescription(
        answer
      );

      this.sendSignal?.({
        type: 'rtc_answer',
        channel_id: this.channelId,
        target_connection_id:
          sourceConnectionId,
        description:
          pc.localDescription.toJSON
            ? pc.localDescription.toJSON()
            : {
                type:
                  pc.localDescription.type,
                sdp:
                  pc.localDescription.sdp,
              },
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async handleAnswer(
    sourceConnectionId,
    description
  ) {
    try {
      const state =
        this.peers.get(
          sourceConnectionId
        );

      if (!state) {
        throw new Error(
          'Received RTC answer for an unknown peer.'
        );
      }

      await state.pc.setRemoteDescription(
        description
      );

      state.remoteDescriptionSet =
        true;

      await this.flushPendingCandidates(
        sourceConnectionId
      );
    } catch (error) {
      this.handleError(error);
    }
  }

  async handleIceCandidate(
    sourceConnectionId,
    candidate
  ) {
    try {
      if (candidate === null) {
        return;
      }

      let state =
        this.peers.get(
          sourceConnectionId
        );

      if (!state) {
        await this.createPeer(
          sourceConnectionId,
          {
            initiator: false,
          }
        );

        state =
          this.peers.get(
            sourceConnectionId
          );
      }

      if (
        !state.remoteDescriptionSet ||
        !state.pc.remoteDescription
      ) {
        state.pendingCandidates.push(
          candidate
        );

        return;
      }

      await state.pc.addIceCandidate(
        candidate
      );
    } catch (error) {
      this.handleError(error);
    }
  }

  async flushPendingCandidates(
    peerConnectionId
  ) {
    const state =
      this.peers.get(
        peerConnectionId
      );

    if (!state) return;

    while (
      state.pendingCandidates.length
    ) {
      const candidate =
        state.pendingCandidates.shift();

      await state.pc.addIceCandidate(
        candidate
      );
    }
  }

  removePeer(peerConnectionId) {
    const state =
      this.peers.get(
        peerConnectionId
      );

    if (!state) return;

    try {
      state.pc.close();
    } catch {
      // Already closed.
    }

    this.peers.delete(
      peerConnectionId
    );
  }

  closeAllPeers() {
    for (
      const peerConnectionId
      of this.peers.keys()
    ) {
      this.removePeer(
        peerConnectionId
      );
    }
  }

  stopMicrophone() {
    if (!this.localStream) return;

    for (
      const track of this.localStream.getTracks()
    ) {
      track.stop();
    }

    this.localStream = null;
  }

  reset() {
    this.setTransmitEnabled(false);
    this.closeAllPeers();
    this.stopMicrophone();

    this.channelId = null;
    this.iceServers = null;
    this.turnExpiresAt = 0;
  }

  handleError(error) {
    console.error(
      '[RapidXCommsRtc]',
      error
    );

    if (this.onError) {
      this.onError(error);
    }
  }
}

export default RapidXCommsRtc;
